/*
 * Domains and DNS (milestone 6), built against docs/milestone-6.md. The API
 * builder documents the routes in docs/api.md under "Domains and DNS
 * (milestone 6)"; these types follow the spec until then.
 *
 * Money is in cents (USD), like the pricing package.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useOrg } from "../app/org";
import { api, qs } from "./client";
import type { EnvName } from "./types";

export type DomainStatus = "pending" | "active" | "transfer_pending" | "expired" | "failed";
export type ZoneStatus = "pending" | "active" | "moved";

export type DnsZone = {
  id: string;
  name: string;
  /** "active" only once Cloudflare says so; "moved": the nameservers point elsewhere now. */
  status: ZoneStatus;
  statusDetail?: string | null;
  /** The nameservers to set at the registrar (Cloudflare's, for this zone). */
  nameservers: string[];
  /** What public DNS delegated to at the last check; null before one. */
  currentNameservers?: string[] | null;
  checkedAt?: string | null;
  activatedAt?: string | null;
  /** An existing zone a platform admin adopted: the records it came with are read-only here. */
  adopted?: boolean;
  createdByUs?: boolean;
  /** "Point to a site" ran while the zone was pending: these names join the environment once it's active. */
  waitingToAttach?: { environmentId: string; names: string[]; detail?: string | null } | null;
};

export type Domain = {
  id: string;
  name: string;
  /** Registered through Hosterr (OpenSRS), or elsewhere with only DNS here. */
  registrar: "opensrs" | "external";
  status: DomainStatus;
  statusDetail?: string | null;
  expiresAt: string | null;
  expiresInDays?: number | null;
  /** Registered here, active, and 30 days or less from expiring. */
  expiringSoon?: boolean;
  /** Registered or transferred in by this panel, so its registration can be changed here. */
  managedHere?: boolean;
  /** Null for a domain registered elsewhere. */
  autoRenew: boolean | null;
  privacy: boolean | null;
  /** One year's renewal, in cents. Null when registered elsewhere. */
  renewalPrice: number | null;
  zone: Pick<DnsZone, "id" | "status" | "nameservers"> | null;
  /** Registered here: what the registry last said (the lock, ICANN's owner check, a transfer away); null until read. */
  registry?: DomainRegistry | null;
  createdAt: string;
  /** What answers at this domain or under it, in its org and, for an agency, its clients. */
  usedBy?: DomainUse;
};

export type DomainUse = {
  /** Environments with a hostname at or under the domain. */
  sites: { siteId: string; siteName: string; siteOrgId?: string; environmentId?: string; environment: "live" | "test" | "dev"; hostnames: string[] }[];
  /** Mail domains at or under it. */
  mail: { mailDomainId: string; domain: string; orgId?: string }[];
};

export type DomainRegistry = {
  /** supported: false where the registry has no transfer lock. */
  lock: { locked: boolean; canChange: boolean; supported: boolean } | null;
  /** ICANN's registrant verification: verified, verifying, pending, not_verified, suspended, unverified, admin_reviewing. */
  verification: { status: string; deadline: string | null; daysToSuspend: number | null; emailBounced: boolean } | null;
  /** A transfer to another registrar: pending_owner, pending_admin, pending_registry, completed, cancelled. */
  transferAway: { status: string; gainingRegistrar: string | null; requestedAt: string | null; ownerChoice: "approved" | "declined" | null } | null;
  /** In auction escrow: it can't be renewed. */
  auctionEscrow: boolean;
  checkedAt: string | null;
};

export type DsRecord = { keyTag: number; algorithm: number; digestType: number; digest: string };
export type DnssecView = {
  /** hosted: DNS is here and Cloudflare signs it; manual: registered here with DNS elsewhere (DS records by hand); null: neither yet. */
  mode: "hosted" | "manual" | null;
  registeredHere: boolean;
  zone: { status: "active" | "pending" | "disabled" | "pending-disabled" | "error"; ds: (DsRecord & { record: string }) | null } | null;
  /** What the registry has; null when it couldn't be read. */
  registry: DsRecord[] | null;
  /** Hosted and registered here: the registry has exactly Cloudflare's DS. */
  inStep: boolean | null;
};
/** A DS change: the registry refuses them on a locked domain, so the transfer lock is lifted for it and put back (lockRestored false: it couldn't be). */
export type DnssecChange = { dnssec: DnssecView; lockLifted: boolean; lockRestored: boolean };
/** canDelete: false while it's in use as a nameserver (the registry keeps it). */
export type HostRecord = { name: string; ipv4: string | null; ipv6: string | null; canDelete: boolean };

export type DomainContact = {
  firstName: string;
  lastName: string;
  orgName: string | null;
  email: string;
  phone: string;
  address1: string;
  address2: string | null;
  city: string;
  state: string;
  postalCode: string;
  /** Two letters, like US. */
  country: string;
};

export type DomainOrder = {
  id: string;
  kind: "register" | "renew" | "transfer";
  name?: string;
  status: "pending" | "completed" | "failed";
  /** The bank wants the card holder to confirm (3-D Secure): send them to paymentUrl; it goes ahead once paid. */
  awaitingPayment?: boolean;
  paymentUrl?: string | null;
  /** Cents. */
  amount: number;
  periodYears: number;
  /** A renewal the registry made on its own, charged on the next invoice. */
  automatic?: boolean;
  createdAt: string;
  completedAt?: string | null;
  error: string | null;
};

export type DomainDetail = Domain & {
  /** What the registry has for the domain now. */
  nameservers: string[];
  /** Some TLDs don't allow WHOIS privacy. */
  privacyAvailable: boolean;
  /** The domain's own registrant (migration 052): null when it's registered elsewhere, or not known yet. */
  contact: DomainContact | null;
  /**
   * Where `contact` came from: the order, the registry, the org's saved contact (the registry couldn't be read yet), or
   * a change made here; and a change the registry doesn't show yet. Null for a domain registered elsewhere.
   */
  registrant?: { source: "order" | "registry" | "org" | "changed" | null; at: string | null; requested: DomainContact | null; requestedAt: string | null; matchesOrgContact: boolean } | null;
  /** The org's saved contact, the default for new registrations. */
  orgContact?: DomainContact | null;
  transfer: { status: "pending" | "completed" | "cancelled" | "failed" | "unknown"; detail: string | null; updatedAt: string } | null;
  zoneDetail?: DnsZone | null;
  orders: DomainOrder[];
  syncedAt?: string | null;
  /** Owners and admins: whether it can be removed now, and what stops it, in words. Null below admin. */
  removal?: { allowed: boolean; reasons: string[] } | null;
};

/** Per year, in cents. transfer is null where transfers in aren't offered; years are the periods the registry takes (.ai: 2 to 10), minYears the first. */
export type DomainPrice = { register: number; renew: number; transfer: number | null; minYears?: number; years?: number[] };

export type DomainSearchResult = {
  name: string;
  available: boolean;
  /** Premium names carry their own, higher price. */
  premium: boolean;
  /** One year, in cents. Null when it can't be registered here. */
  price: DomainPrice | null;
  /** Why it can't be registered here, in words. */
  reason: string | null;
  /** A suggestion rather than the name searched for. */
  suggestion?: boolean;
  /** available (at `price`), taken (still listed), premium (available at the registry's higher price), unavailable (`reason` says why). */
  availability?: DomainAvailability;
  /** Taken, but it could be transferred in here. */
  transferable?: boolean;
};

export type DomainAvailability = "available" | "taken" | "premium" | "unavailable";

/** The API's `availability`, or worked out from the older booleans. */
export function availabilityOf(r: DomainSearchResult): { availability: DomainAvailability; transferable: boolean } {
  const availability = r.availability ?? (r.available ? (r.premium ? "premium" : "available") : r.price ? "taken" : "unavailable");
  return { availability, transferable: r.transferable ?? (availability === "taken" && !!r.price) };
}

export type DnsType = "A" | "AAAA" | "CNAME" | "MX" | "TXT" | "CAA" | "SRV" | "NS";

export type DnsUsedBy =
  | { kind: "environment"; siteId: string; siteName: string; environment: EnvName; environmentId?: string; /** "Live", or an extra environment's name. */ environmentLabel?: string }
  | { kind: "mail"; mailDomainId: string; domain: string }
  | { kind: "preview"; label: string };

export type DnsRecord = {
  id: string;
  type: DnsType;
  /** Fully qualified: "www.acme.com", or "acme.com" for the root. */
  name: string;
  content: string;
  /** Seconds; 1 means automatic. */
  ttl: number;
  priority: number | null;
  /** Through Cloudflare's proxy (orange cloud). Only A, AAAA and CNAME can be. */
  proxied: boolean | null;
  /** Relative: "@", "www". */
  host?: string;
  /** Made through the panel. */
  ours: boolean;
  purpose: "site" | "mail" | "preview" | "manual";
  /** Made by the panel for a site or mail: developers may change it, after a warning. */
  managed?: boolean;
  usedBy: DnsUsedBy | null;
  /** "Used by Live", "Used by mail". */
  label?: string | null;
  /** Came with an adopted zone, or was added in Cloudflare directly: nobody changes it here. */
  readOnly?: boolean;
  readOnlyReason?: string | null;
  /** SRV and CAA parts, as Cloudflare keeps them. */
  data?: Record<string, string | number> | null;
  /** Why it's there, for the next person. Kept by the panel only (never at Cloudflare), so read-only records can have one. */
  note?: string | null;
  modifiedAt?: string | null;
  /** Whether the person asking can change it (its value and note; admins also its name and type): a managed one takes manage on its environment. */
  canChange?: boolean;
};

export type DnsRecordInput = {
  type: DnsType;
  name: string;
  content: string;
  ttl: number;
  priority?: number | null;
  proxied?: boolean;
  data?: Record<string, string | number> | null;
  /** Up to 500 characters; "" for none. */
  note?: string;
};

export type DnsChange = {
  /** delete: a record the panel made for this purpose that's no longer right. conflict: someone else's record in the way, never changed. */
  action: "create" | "update" | "delete" | "keep" | "conflict";
  type: DnsType;
  name: string;
  content: string;
  ttl?: number;
  priority?: number | null;
  /** For an update: what's there now. */
  before?: { content: string; ttl?: number; proxied?: boolean | null; priority?: number | null } | null;
  recordId?: string | null;
  /** Why, in words; for a conflict, what to change by hand in the editor. */
  reason?: string | null;
  /** For a conflict: what the panel would set there. */
  want?: { type: DnsType; content: string; priority: number | null } | null;
};

export type DnsPlan = {
  kind: "site" | "mail";
  /** Sent back as planId when applying: a plan that changed since is refused (409 plan_changed). */
  id?: string;
  zoneStatus?: ZoneStatus;
  /** "Acme Bakery (Live) on web-1, 203.0.113.5", or "Mail for acme.com on mail-1". */
  target: string;
  changes: DnsChange[];
  /** What happens after the records: "Then acme.com and www.acme.com are added to Live, which gets them a certificate." */
  then?: string | null;
  /** Mail: SPF and DKIM pass the mail DNS check and DMARC is p=none (or missing): the step to p=quarantine it advises, as its own plan. */
  dmarcStep?: { value: string } | null;
};

/** Mail's `dmarc: "quarantine"`: the explicit step from p=none, once SPF and DKIM pass. */
export type OneClick = { kind: "site"; environmentId: string } | { kind: "mail"; mailDomainId: string; dmarc?: "quarantine" };

/** A record found in the domain's current DNS, offered for the new zone (a one-click "create", with what the panel suggests). */
export type ImportRecord = DnsChange & { id: string; action: "create"; data: Record<string, string | number> | null; keep: boolean };
export type ImportPlan = {
  domain: string;
  /** Where its DNS is now; null when that couldn't be looked up. */
  nameservers: string[] | null;
  /** "GoDaddy", "Cloudflare", "another provider". */
  provider: string | null;
  records: ImportRecord[];
  /** Found but can't be added here, with why. */
  skipped: { type: string; name: string; content: string; reason: string }[];
  /** "blog.acme.com (A)": lookups that didn't answer. */
  unanswered: string[];
  /** Names whose addresses are Cloudflare's proxy (the site is behind Cloudflare in another account): left out, the server's own address needed. */
  behindProxy?: string[];
  checked: number;
};
/** What adding the records did: some can fail (Cloudflare refused one). */
export type Copied = { created: number; failed: { record: { type: string; name: string; content: string }; error: string }[] };

/** What the one-clicks can point at (mail domains: this domain or below it). */
export type DnsTargets = {
  /** server: null for people who don't see servers (docs/plans.md). */
  environments: { id: string; siteId: string; siteName: string; environment: EnvName; label: string; server: string | null; ipv4: string | null; ipv6: string | null }[];
  mailDomains: { id: string; domain: string; server: string | null }[];
};

export type DomainQuote = { name: string; kind: "register" | "renew" | "transfer"; periodYears: number; amount: number; renewal: number; premium: boolean; currency: string };

export const domainKeys = {
  all: (orgId: string) => ["org", orgId, "domains"] as const,
  list: (orgId: string) => ["org", orgId, "domains", "list"] as const,
  one: (orgId: string, id: string) => ["org", orgId, "domains", "one", id] as const,
  dns: (orgId: string, id: string) => ["org", orgId, "domains", "one", id, "dns"] as const,
  targets: (orgId: string, id: string) => ["org", orgId, "domains", "one", id, "targets"] as const,
  quote: (orgId: string, name: string, kind: string, years: number) => ["org", orgId, "domains", "quote", name, kind, years] as const,
  plan: (orgId: string, id: string, c: OneClick) => ["org", orgId, "domains", "one", id, "plan", c] as const,
  search: (orgId: string, q: string) => ["org", orgId, "domains", "search", q] as const,
  contact: (orgId: string) => ["org", orgId, "domains", "contact"] as const,
  dnssec: (orgId: string, id: string) => ["org", orgId, "domains", "one", id, "dnssec"] as const,
  hosts: (orgId: string, id: string) => ["org", orgId, "domains", "one", id, "hosts"] as const,
};

const base = (orgId: string) => `/orgs/${orgId}/domains`;

/** `accounts`: every account's domains too (a platform admin on the platform org; app/accounts.tsx). */
export function useDomains(enabled = true, opts: { accounts?: boolean } = {}) {
  const org = useOrg();
  const across = !!opts.accounts;
  return useQuery({
    queryKey: across ? ([...domainKeys.list(org.id), "accounts"] as const) : domainKeys.list(org.id),
    queryFn: ({ signal }) => api.get<{ domains: Domain[] }>(`${base(org.id)}${across ? "?accounts=all" : ""}`, signal),
    select: (d) => d.domains,
    enabled,
    refetchInterval: across ? 60_000 : undefined,
  });
}

export function useDomain(id: string) {
  const org = useOrg();
  return useQuery({
    queryKey: domainKeys.one(org.id, id),
    queryFn: ({ signal }) => api.get<{ domain: DomainDetail }>(`${base(org.id)}/${id}`, signal),
    select: (d) => d.domain,
  });
}

/** The name searched for (a bare word is its .com), with its prices; related names come from useDomainSuggestions (api/domainPrices.ts). */
export function useDomainSearch(q: string) {
  const org = useOrg();
  const term = q.trim().toLowerCase();
  return useQuery({
    queryKey: domainKeys.search(org.id, term),
    queryFn: ({ signal }) => api.get<{ query: string; results: DomainSearchResult[] }>(`${base(org.id)}/search${qs({ q: term })}`, signal),
    enabled: term.length >= 2,
    staleTime: 60_000,
  });
}

/** The exact amount to confirm for a period (OpenSRS's own price when it's higher than a year times the period). */
export function useDomainQuote(name: string, kind: "register" | "renew" | "transfer", years: number, enabled = true) {
  const org = useOrg();
  return useQuery({
    queryKey: domainKeys.quote(org.id, name, kind, years),
    queryFn: ({ signal }) => api.get<{ quote: DomainQuote }>(`${base(org.id)}/quote${qs({ name, kind, years })}`, signal),
    select: (d) => d.quote,
    enabled: enabled && !!name,
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * The org's own default registrant for new registrations, with who saved it, and for a client its agency's
 * (`agency`), offered as a choice that fills the form: nothing uses it unless it's sent.
 */
export type DomainContactInfo = { contact: DomainContact | null; updatedAt: string | null; updatedBy: string | null; agency: { name: string; contact: DomainContact } | null };
export function useDomainContact(enabled = true) {
  const org = useOrg();
  return useQuery({
    queryKey: domainKeys.contact(org.id),
    queryFn: ({ signal }) => api.get<DomainContactInfo>(`${base(org.id)}/contact`, signal),
    select: (d): DomainContactInfo => ({ contact: d.contact ?? null, updatedAt: d.updatedAt ?? null, updatedBy: d.updatedBy ?? null, agency: d.agency ?? null }),
    enabled,
  });
}

/** Registering or transferring costs money: the body carries the price the person saw and an idempotency key. */
export type BuyInput = {
  name: string;
  periodYears: number;
  privacy: boolean;
  autoRenew: boolean;
  contact: DomainContact;
  /** Keep `contact` as the org's default for new domains once the order is placed (without a default, the first is kept anyway). */
  saveAsDefault?: boolean;
  /** The total in cents the person confirmed; a different price now is a 409 with the new one. */
  expectedAmount: number;
  idempotencyKey: string;
  authCode?: string;
  /** The card to charge (owners and billing people choose); the account's default when left out. */
  paymentMethodId?: string;
};

/** The bank wants the card holder to confirm (3-D Secure): what Stripe.js needs to show its check in place. */
export type OrderPayment = { clientSecret: string; publishableKey: string } | null;

export function useRegisterDomain() {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ authCode, ...v }: BuyInput) =>
      authCode
        ? api.post<{ domain: DomainDetail; order: DomainOrder; payment?: OrderPayment }>(`${base(org.id)}/transfer`, { ...v, authCode })
        : api.post<{ domain: DomainDetail; order: DomainOrder; payment?: OrderPayment }>(base(org.id), v),
    onSettled: () => qc.invalidateQueries({ queryKey: domainKeys.all(org.id) }),
  });
}

// Domain prices, suggestions and the platform admins' prices page: api/domainPrices.ts.

export type BatchItem = { name: string; periodYears: number; privacy: boolean; autoRenew: boolean; expectedAmount: number };
export type BatchOrder = DomainOrder & { paid: boolean; refunded: boolean };
export type BatchResult = {
  orders: BatchOrder[];
  domains: Domain[];
  /** The bank wants the card holder: all of them wait until it's confirmed. */
  paymentUrl: string | null;
  registered: number;
  failed: number;
  chargedAmount: number;
  refundedAmount: number;
  /** The bank's check, to show in place. */
  payment?: OrderPayment;
};

/** Several registrations in one order and one charge. A name the registrar refuses is refunded on its own. */
export function useRegisterBatch() {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { items: BatchItem[]; idempotencyKey: string; contact?: DomainContact; saveAsDefault?: boolean; paymentMethodId?: string }) => api.post<BatchResult>(`${base(org.id)}/batch`, v),
    onSettled: () => qc.invalidateQueries({ queryKey: domainKeys.all(org.id) }),
  });
}

/** A domain registered elsewhere: Hosterr hosts its DNS, with the records kept from its plan created first. No charge. */
export function useAddExternalDomain() {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { name: string; records: DnsRecordInput[] }) => api.post<{ domain: Domain; copied: Copied }>(`${base(org.id)}/external`, v),
    onSettled: () => qc.invalidateQueries({ queryKey: domainKeys.all(org.id) }),
  });
}

/** The records a domain has now (public DNS), before its DNS moves here: for a name not in the panel yet, or a domain that is. */
export function useImportPlan(target: { name: string } | { domainId: string } | null, names: string[] = []) {
  const org = useOrg();
  const extra = names.join(",");
  return useQuery({
    // Not under domainKeys.all: adding the domain invalidates those, and this would look it all up again.
    queryKey: ["org", org.id, "domain-import", target, extra] as const,
    queryFn: ({ signal }) =>
      api.get<{ plan: ImportPlan }>(
        target && "domainId" in target ? `${base(org.id)}/${target.domainId}/dns/import${qs({ names: extra || undefined })}` : `${base(org.id)}/import${qs({ name: target?.name ?? "", names: extra || undefined })}`,
        signal,
      ),
    select: (d) => d.plan,
    enabled: !!target,
    staleTime: 60_000,
    retry: false,
    // A new lookup (a name added) keeps showing the last list until it answers.
    placeholderData: (prev) => prev,
  });
}

/** The zone file of a domain's records, as a download (the API sends it as an attachment). */
export const dnsExportUrl = (orgId: string, domainId: string) => `/api${base(orgId)}/${domainId}/dns/export`;

export function useUpdateDomain(id: string) {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { autoRenew?: boolean; privacy?: boolean; nameservers?: string[]; transferLock?: boolean }) => api.patch<{ domain: DomainDetail }>(`${base(org.id)}/${id}`, v),
    onSuccess: (r) => qc.setQueryData(domainKeys.one(org.id, id), r),
    onSettled: () => qc.invalidateQueries({ queryKey: domainKeys.all(org.id) }),
  });
}

export function useSaveContact(domainId: string | null) {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (contact: DomainContact) =>
      // A domain's: `waiting` when the registry hasn't shown the change yet (a change of registrant being confirmed).
      domainId ? api.put<{ contact: DomainContact; waiting?: boolean }>(`${base(org.id)}/${domainId}/contact`, { contact }) : api.put<{ contact: DomainContact; waiting?: boolean }>(`${base(org.id)}/contact`, { contact }),
    onSettled: () => qc.invalidateQueries({ queryKey: domainKeys.all(org.id) }),
  });
}

/* ── The controls beyond DNS ────────────────────────────────────── */

/** The auth (EPP) code for a transfer out; the API refuses while the lock is on, and says who asked in Activity. */
export function useAuthCode(id: string) {
  const org = useOrg();
  return useMutation({ mutationFn: () => api.post<{ authCode: string | null }>(`${base(org.id)}/${id}/auth-code`) });
}

export function useResendVerification(id: string) {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ verification: DomainRegistry["verification"] }>(`${base(org.id)}/${id}/verification/send`),
    onSettled: () => qc.invalidateQueries({ queryKey: domainKeys.all(org.id) }),
  });
}

export function useDnssec(id: string, enabled = true) {
  const org = useOrg();
  return useQuery({
    queryKey: domainKeys.dnssec(org.id, id),
    queryFn: ({ signal }) => api.get<{ dnssec: DnssecView }>(`${base(org.id)}/${id}/dnssec`, signal),
    select: (d) => d.dnssec,
    enabled,
    // Cloudflare says "pending" until it sees the DS at the registry: look again now and then while it does.
    refetchInterval: (q) => (q.state.data?.dnssec.zone?.status === "pending" || q.state.data?.dnssec.zone?.status === "pending-disabled" ? 60_000 : false),
  });
}

/** DNS hosted here: turn DNSSEC on or off (the DS record follows at the registry when it's registered here). */
export function useSetDnssec(id: string) {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (on: boolean) => api.post<DnssecChange>(`${base(org.id)}/${id}/dnssec`, { on }),
    onSuccess: (r) => qc.setQueryData(domainKeys.dnssec(org.id, id), { dnssec: r.dnssec }),
    // The lock may have been lifted for the change: the domain's registry view too.
    onSettled: () => qc.invalidateQueries({ queryKey: domainKeys.one(org.id, id) }),
  });
}

/** DNS elsewhere: the whole set of DS records the registry should have. */
export function useSetDsRecords(id: string) {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (records: DsRecord[]) => api.put<DnssecChange>(`${base(org.id)}/${id}/dnssec/records`, { records }),
    onSuccess: (r) => qc.setQueryData(domainKeys.dnssec(org.id, id), { dnssec: r.dnssec }),
    onSettled: () => qc.invalidateQueries({ queryKey: domainKeys.one(org.id, id) }),
  });
}

export function useHosts(id: string, enabled = true) {
  const org = useOrg();
  return useQuery({
    queryKey: domainKeys.hosts(org.id, id),
    queryFn: ({ signal }) => api.get<{ hosts: HostRecord[] }>(`${base(org.id)}/${id}/hosts`, signal),
    select: (d) => d.hosts,
    enabled,
  });
}

/** Add a child nameserver, or change one's addresses (`existing`: its full name). */
export function useSaveHost(id: string) {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ existing, ...v }: { existing?: string; name: string; ipv4: string | null; ipv6: string | null }) =>
      existing
        ? api.patch<{ host: HostRecord; hosts: HostRecord[] }>(`${base(org.id)}/${id}/hosts/${encodeURIComponent(existing)}`, { ipv4: v.ipv4, ipv6: v.ipv6 })
        : api.post<{ host: HostRecord; hosts: HostRecord[] }>(`${base(org.id)}/${id}/hosts`, v),
    onSuccess: (r) => qc.setQueryData(domainKeys.hosts(org.id, id), { hosts: r.hosts }),
  });
}

export function useDeleteHost(id: string) {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.del<{ hosts: HostRecord[] }>(`${base(org.id)}/${id}/hosts/${encodeURIComponent(name)}`),
    onSuccess: (r) => qc.setQueryData(domainKeys.hosts(org.id, id), { hosts: r.hosts }),
  });
}

export function useRenewDomain(id: string) {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { periodYears: number; expectedAmount: number; idempotencyKey: string; paymentMethodId?: string }) => api.post<{ domain: DomainDetail; order: DomainOrder; payment?: OrderPayment }>(`${base(org.id)}/${id}/renew`, v),
    onSettled: () => qc.invalidateQueries({ queryKey: domainKeys.all(org.id) }),
  });
}

export function useRemoveDomain(id: string) {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.del<{ ok: true }>(`${base(org.id)}/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: domainKeys.list(org.id) }),
  });
}

/* ── DNS ────────────────────────────────────────────────────────── */

/** Host the DNS here for a domain that isn't yet: the records kept from its plan first, then (registered here) the nameservers, unless one failed. */
export function useHostZone(domainId: string) {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (records: DnsRecordInput[]) => api.post<{ domain: DomainDetail; copied: Copied; switched: boolean }>(`${base(org.id)}/${domainId}/dns/zone`, { records }),
    onSettled: () => qc.invalidateQueries({ queryKey: domainKeys.all(org.id) }),
  });
}

/** Read the registration and the zone again now. */
export function useRefreshDomain(domainId: string) {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ domain: DomainDetail }>(`${base(org.id)}/${domainId}/refresh`),
    onSettled: () => qc.invalidateQueries({ queryKey: domainKeys.one(org.id, domainId) }),
  });
}

/** `orgId`: the zone's org when it isn't the current one (an agency's zone, from its client's mail domain). */
export function useDnsTargets(domainId: string, enabled = true, orgId?: string) {
  const current = useOrg();
  const org = { id: orgId ?? current.id };
  return useQuery({
    queryKey: domainKeys.targets(org.id, domainId),
    queryFn: ({ signal }) => api.get<DnsTargets>(`${base(org.id)}/${domainId}/dns/targets`, signal),
    enabled,
  });
}

export function useDns(domainId: string, opts: { poll?: boolean } = {}) {
  const org = useOrg();
  return useQuery({
    queryKey: domainKeys.dns(org.id, domainId),
    queryFn: ({ signal }) => api.get<{ zone: DnsZone; records: DnsRecord[]; stale?: boolean }>(`${base(org.id)}/${domainId}/dns`, signal),
    // A pending zone is checked by the server every 15 minutes; the page looks again every minute.
    refetchInterval: opts.poll ? 60_000 : false,
  });
}

export function useCheckZone(domainId: string) {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ zone: DnsZone }>(`${base(org.id)}/${domainId}/dns/check`),
    onSettled: () => qc.invalidateQueries({ queryKey: domainKeys.one(org.id, domainId) }),
  });
}

/** A new or changed record (PATCH takes any subset: `{ note }` alone changes only the note, without Cloudflare). */
export function useSaveRecord(domainId: string) {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...v }: Partial<DnsRecordInput> & { id?: string }) =>
      id
        ? api.patch<{ record: DnsRecord }>(`${base(org.id)}/${domainId}/dns/records/${id}`, v)
        : api.post<{ record: DnsRecord }>(`${base(org.id)}/${domainId}/dns/records`, v),
    // The row shows what was saved at once; the refetch after brings the rest of the zone along.
    onSuccess: ({ record }) =>
      qc.setQueryData<{ zone: DnsZone; records: DnsRecord[]; stale?: boolean }>(domainKeys.dns(org.id, domainId), (d) =>
        d ? { ...d, records: d.records.some((r) => r.id === record.id) ? d.records.map((r) => (r.id === record.id ? record : r)) : [...d.records, record] } : d,
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: domainKeys.dns(org.id, domainId) }),
  });
}

export function useDeleteRecord(domainId: string) {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: true }>(`${base(org.id)}/${domainId}/dns/records/${id}`),
    onSettled: () => qc.invalidateQueries({ queryKey: domainKeys.dns(org.id, domainId) }),
  });
}

/** The diff for a one-click: what would be created, updated or left alone. Nothing changes until it's applied. */
export function useDnsPlan(domainId: string, c: OneClick | null, orgId?: string) {
  const current = useOrg();
  const org = { id: orgId ?? current.id };
  return useQuery({
    queryKey: domainKeys.plan(org.id, domainId, c ?? { kind: "mail", mailDomainId: "" }),
    queryFn: ({ signal }) =>
      api.get<{ plan: DnsPlan }>(
        `${base(org.id)}/${domainId}/dns/plan${qs(c?.kind === "site" ? { kind: "site", environmentId: c.environmentId } : { kind: "mail", mailDomainId: c?.kind === "mail" ? c.mailDomainId : "", dmarc: c?.kind === "mail" ? c.dmarc : undefined })}`,
        signal,
      ),
    select: (d) => d.plan,
    enabled: !!c,
    staleTime: 0,
  });
}

export function useApplyPlan(domainId: string, orgId?: string) {
  const current = useOrg();
  const org = { id: orgId ?? current.id };
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: OneClick & { planId?: string }) =>
      api.post<{ plan: DnsPlan; applied: number; failed: { change: DnsChange; error: string }[]; operationId: string | null; records: DnsRecord[] }>(`${base(org.id)}/${domainId}/dns/apply`, v),
    onSettled: () => qc.invalidateQueries({ queryKey: domainKeys.one(org.id, domainId) }),
  });
}
